/**
 * mtfSynthesis — bias / zone / trigger synthesis from MultiTfState.
 *
 * Implements the Shannon + Grimes hybrid framework from the Top-Down
 * design doc (`docs/research/Coach_TopDown_Analysis.md` §3):
 *
 *   - **BIAS** from Daily + 4H (primary + intermediate trend roles).
 *     Aggregates stage, EMA stack, always-in, trend integrity into a
 *     directional score; classifies as long / short / mixed / unclear.
 *   - **ZONE** from 1H + 15M (where structure currently is). Combines
 *     stage + EMA stack stance + RSI position into one of: pullback-
 *     in-trend / range-bound / level-test / breakout-imminent / unclear.
 *   - **TRIGGER** from 5M (entry timing). Always-in + RSI signal cross
 *     + EMA stack alignment with HTF bias.
 *   - **COHERENCE** grade A+ → F based on bias / trigger directional
 *     alignment + zone supportiveness.
 *
 * Composes cleanly with v7.5.14 live-trade gate (callers should still
 * guard their cohort builds) and v7.5.13 direction-cohort contract
 * (every cohort downstream of synthesis stays direction-filtered).
 *
 * Pure function. No side effects. Tolerant to partially-populated
 * MultiTfState — missing TFs are scored as neutral, not as errors.
 */

import { TF_KEYS } from '../../barAnalysis/multiTfState.js';

/**
 * Score one TF for directional bias. Returns a signed integer:
 *   - Positive = bullish lean (markup + bullish stack + long always-in + intact uptrend)
 *   - Negative = bearish lean
 *   - 0 = neutral / unclear / no data
 *
 * Each component contributes independently — no single signal can
 * dominate. Helpers must NOT diverge from their classifier conventions
 * (stage values, alwaysIn values, emaStack values) so future tuning of
 * thresholds stays local to the classifiers themselves.
 *
 * Max possible: +5 (markup +2, alwaysIn +1, stack +1, integrity-up +1).
 * Min possible: -5.
 */
export const scoreTimeframeBias = (tfState) => {
  if (!tfState || tfState.barsAvailable === 0) return 0;
  let score = 0;
  // Stage carries the most weight — it's the most direct read on cycle position.
  if (tfState.stage === 'markup') score += 2;
  else if (tfState.stage === 'markdown') score -= 2;
  // Other axes contribute equally — each is one signal among many.
  if (tfState.alwaysIn === 'long') score += 1;
  else if (tfState.alwaysIn === 'short') score -= 1;
  if (tfState.emaStack === 'bullish') score += 1;
  else if (tfState.emaStack === 'bearish') score -= 1;
  // Trend integrity reads as bullish when alwaysIn is long AND trend is intact
  // (an intact down-trend is bearish; this captures that conjunction).
  if (tfState.trendIntegrity === 'intact') {
    if (tfState.alwaysIn === 'long') score += 1;
    else if (tfState.alwaysIn === 'short') score -= 1;
  }
  return score;
};

/**
 * Derive directional bias from the Daily + 4H state — Shannon's primary +
 * intermediate trend roles. Returns the bias direction, a 0–1 confidence
 * value, and a human-readable rationale string.
 *
 * Thresholds:
 *   |total score| ≥ 7 → high-conviction direction (out of max ±10)
 *   |total score| ≥ 4 → directional bias
 *   |total score| ≥ 2 → mixed (some signal but split)
 *   |total score| < 2 → unclear
 */
export const deriveBias = (multiTfState) => {
  const daily = multiTfState?.daily;
  const fourHour = multiTfState?.fourHour;
  const dailyScore = scoreTimeframeBias(daily);
  const fourHourScore = scoreTimeframeBias(fourHour);
  const total = dailyScore + fourHourScore;
  const maxScore = 10;  // 5 per TF, 2 TFs

  let direction, confidence, rationale;
  if (total >= 4) {
    direction = 'long';
    confidence = Math.min(1, Math.abs(total) / maxScore);
    rationale = `Daily ${daily?.stage ?? 'unclear'} + 4H ${fourHour?.stage ?? 'unclear'}; ` +
      `EMA stacks ${daily?.emaStack ?? '?'} / ${fourHour?.emaStack ?? '?'}; ` +
      `total score +${total}`;
  } else if (total <= -4) {
    direction = 'short';
    confidence = Math.min(1, Math.abs(total) / maxScore);
    rationale = `Daily ${daily?.stage ?? 'unclear'} + 4H ${fourHour?.stage ?? 'unclear'}; ` +
      `EMA stacks ${daily?.emaStack ?? '?'} / ${fourHour?.emaStack ?? '?'}; ` +
      `total score ${total}`;
  } else if (Math.abs(total) >= 2) {
    direction = 'mixed';
    confidence = Math.abs(total) / maxScore;
    rationale = `Mixed: Daily score ${dailyScore}, 4H score ${fourHourScore}`;
  } else {
    direction = 'unclear';
    confidence = 0;
    rationale = 'No clear HTF bias — Daily + 4H signals net to zero';
  }
  return { direction, confidence, dailyScore, fourHourScore, totalScore: total, rationale };
};

/**
 * Derive zone state from 1H + 15M state.
 *
 * Returns one of:
 *   - 'pullback-in-trend' — HTF bias clear; LTF stack against bias (price retracing)
 *   - 'breakout-imminent' — HTF bias clear; LTF stack aligned + RSI strong (continuation)
 *   - 'range-bound'      — HTF bias unclear / mixed; LTF stages accumulation/distribution
 *   - 'level-test'       — RSI in extreme zone (potential reversal/test)
 *   - 'unclear'          — default
 *
 * The bias parameter from deriveBias is required — zone classification
 * is relative to the established bias, not absolute. Without bias, the
 * zone falls to 'unclear' or 'range-bound' depending on LTF state.
 */
export const deriveZone = (multiTfState, bias) => {
  const oneHour = multiTfState?.oneHour;
  const fifteen = multiTfState?.fifteen;
  const biasDir = bias?.direction;

  // Aggregate LTF EMA stack stance.
  const stackVote = (tf) =>
    tf?.emaStack === 'bullish' ? 1 :
    tf?.emaStack === 'bearish' ? -1 : 0;
  const ltfStackSum = stackVote(oneHour) + stackVote(fifteen);

  // RSI extremes on either LTF.
  const inExtreme = (tf) =>
    tf?.rsi?.zone === 'overbought' || tf?.rsi?.zone === 'oversold';
  const ltfExtreme = inExtreme(oneHour) || inExtreme(fifteen);

  // Stage signals.
  const ltfRanging = (tf) =>
    tf?.stage === 'accumulation' || tf?.stage === 'distribution';
  const ltfStagesRanging = ltfRanging(oneHour) && ltfRanging(fifteen);

  // ── Decision tree ──────────────────────────────────────────────
  // Range-bound takes precedence when HTF bias is unclear AND LTF
  // stages are in the consolidation phase — there's literally no
  // directional setup to call a pullback or breakout against.
  if ((biasDir === 'unclear' || biasDir === 'mixed') && ltfStagesRanging) {
    return { type: 'range-bound', rationale: '1H + 15M both in accumulation/distribution, no HTF bias' };
  }

  // Level test: RSI extreme on either LTF — bias-independent signal
  // that a reaction is likely.
  if (ltfExtreme) {
    return { type: 'level-test', rationale: '1H or 15M RSI in OB/OS — reaction zone' };
  }

  // Bias-dependent classifications below require a directional bias.
  if (biasDir !== 'long' && biasDir !== 'short') {
    return { type: 'unclear', rationale: 'No directional bias — zone classification not applicable' };
  }

  // Pullback-in-trend: HTF bias is one direction, LTF stack is the other.
  if (biasDir === 'long' && ltfStackSum <= -1) {
    return { type: 'pullback-in-trend', rationale: 'HTF bias long but LTF stack bearish — pullback in uptrend' };
  }
  if (biasDir === 'short' && ltfStackSum >= 1) {
    return { type: 'pullback-in-trend', rationale: 'HTF bias short but LTF stack bullish — pullback in downtrend' };
  }

  // Breakout-imminent: HTF bias clear AND LTF stack aligned.
  if (biasDir === 'long' && ltfStackSum >= 1) {
    return { type: 'breakout-imminent', rationale: 'HTF bias long + LTF stack bullish — continuation imminent' };
  }
  if (biasDir === 'short' && ltfStackSum <= -1) {
    return { type: 'breakout-imminent', rationale: 'HTF bias short + LTF stack bearish — continuation imminent' };
  }

  return { type: 'unclear', rationale: 'LTF state ambiguous relative to HTF bias' };
};

/**
 * Derive trigger status from the 5M state.
 *
 * Returns one of:
 *   - 'fired'    — strong directional signal on 5M (always-in resolved, EMA + RSI aligned)
 *   - 'forming'  — partial signal (RSI in extreme, or recent signal-line cross)
 *   - 'absent'   — no entry trigger
 *
 * `direction` is the trigger's directional vote (long / short / null).
 * Returns null when no 5M data is available.
 */
export const deriveTrigger = (multiTfState) => {
  const five = multiTfState?.five;
  if (!five || five.barsAvailable === 0) {
    return { status: 'absent', direction: null, rationale: '5M state unavailable' };
  }

  const stackBullish = five.emaStack === 'bullish';
  const stackBearish = five.emaStack === 'bearish';
  const alwaysInLong = five.alwaysIn === 'long';
  const alwaysInShort = five.alwaysIn === 'short';
  const rsiSignalBullish = five.rsi?.signalCross === 'bullish';
  const rsiSignalBearish = five.rsi?.signalCross === 'bearish';
  const rsiExtreme = five.rsi?.zone === 'overbought' || five.rsi?.zone === 'oversold';

  // Fired: stack + always-in + (rsi cross OR rsi slope same direction).
  if (stackBullish && alwaysInLong && (rsiSignalBullish || five.rsi?.slope === 'rising')) {
    return { status: 'fired', direction: 'long', rationale: '5M stack + always-in + RSI all bullish' };
  }
  if (stackBearish && alwaysInShort && (rsiSignalBearish || five.rsi?.slope === 'falling')) {
    return { status: 'fired', direction: 'short', rationale: '5M stack + always-in + RSI all bearish' };
  }

  // Forming: RSI cross OR RSI extreme (early read) without full stack alignment.
  if (rsiSignalBullish || (rsiExtreme && five.rsi?.zone === 'oversold')) {
    return { status: 'forming', direction: 'long', rationale: '5M RSI suggests long forming' };
  }
  if (rsiSignalBearish || (rsiExtreme && five.rsi?.zone === 'overbought')) {
    return { status: 'forming', direction: 'short', rationale: '5M RSI suggests short forming' };
  }

  return { status: 'absent', direction: null, rationale: '5M shows no entry trigger' };
};

/**
 * Grade the coherence of bias + zone + trigger — the final "do they
 * agree?" score. Returns one of: A+ / A / B / C / D / F / no-grade.
 *
 * Per design doc §3.2:
 *   A+ — bias matches trigger direction + zone is breakout-imminent /
 *        pullback-in-trend / level-test (all supportive of entry)
 *   A  — bias matches trigger; zone is OK but not ideal
 *   B  — bias matches trigger; zone unclear
 *   C  — bias mixed; trigger fired
 *   D  — bias OPPOSES trigger (low conviction bias)
 *   F  — bias strongly opposes trigger (high conviction counter-bias)
 *   no-grade — no trigger fired or forming
 */
export const gradeCoherence = (bias, zone, trigger) => {
  if (!trigger || trigger.status === 'absent') {
    return { grade: 'no-grade', rationale: 'No 5M trigger — coherence not applicable' };
  }
  if (!bias || bias.direction === 'unclear') {
    return { grade: 'C', rationale: 'Trigger fired but no clear HTF bias to grade against' };
  }

  const biasDir = bias.direction;
  const triggerDir = trigger.direction;

  // Direction match — bias and trigger agree.
  if (biasDir === triggerDir || (biasDir === 'mixed' && triggerDir)) {
    if (biasDir === 'mixed') {
      return { grade: 'C', rationale: 'Trigger direction set but bias is mixed' };
    }
    const supportiveZones = ['breakout-imminent', 'pullback-in-trend', 'level-test'];
    if (zone && supportiveZones.includes(zone.type)) {
      return { grade: 'A+', rationale: `${biasDir.toUpperCase()} bias + ${triggerDir.toUpperCase()} trigger + ${zone.type} zone — full alignment` };
    }
    if (zone && zone.type !== 'unclear') {
      return { grade: 'A', rationale: `${biasDir.toUpperCase()} bias + matching trigger; zone ${zone.type}` };
    }
    return { grade: 'B', rationale: `Bias + trigger direction match; zone unclear` };
  }

  // Direction mismatch — counter-bias entry.
  // F vs D depends on bias confidence.
  if (bias.confidence >= 0.6) {
    return { grade: 'F', rationale: `HIGH-conviction ${biasDir.toUpperCase()} bias opposed by ${triggerDir?.toUpperCase()} trigger — counter-trend chase` };
  }
  return { grade: 'D', rationale: `${biasDir.toUpperCase()} bias opposed by ${triggerDir?.toUpperCase()} trigger — counter-trend risk` };
};

/**
 * Top-level entry point — synthesize the full MTF context from a
 * MultiTfState in one call.
 *
 * @param {Object} multiTfState  Output of computeMultiTfState() from
 *   barAnalysis/multiTfState.js.
 * @param {Object} [opts]        Reserved for per-instrument tuning.
 * @returns {{
 *   bias: { direction, confidence, ... },
 *   zone: { type, rationale },
 *   trigger: { status, direction, rationale },
 *   coherence: 'A+' | 'A' | 'B' | 'C' | 'D' | 'F' | 'no-grade',
 *   coherenceRationale: string,
 * }}
 */
export const synthesizeMtfContext = (multiTfState, opts = {}) => {
  // Defensive: empty / null state → fully-null synthesis.
  if (!multiTfState || typeof multiTfState !== 'object') {
    return {
      bias: { direction: 'unclear', confidence: 0, rationale: 'No multi-TF state provided' },
      zone: { type: 'unclear', rationale: 'No multi-TF state provided' },
      trigger: { status: 'absent', direction: null, rationale: 'No multi-TF state provided' },
      coherence: 'no-grade',
      coherenceRationale: 'No multi-TF state provided',
    };
  }

  const bias = deriveBias(multiTfState);
  const zone = deriveZone(multiTfState, bias);
  const trigger = deriveTrigger(multiTfState);
  const coherenceResult = gradeCoherence(bias, zone, trigger);

  return {
    bias,
    zone,
    trigger,
    coherence: coherenceResult.grade,
    coherenceRationale: coherenceResult.rationale,
  };
};

// Re-export for tests that want to introspect.
export { TF_KEYS };
